import React, { createContext, useState, useEffect, useRef } from "react";
import { jwtDecode } from "jwt-decode";
import { getTutorProfileStatus } from "../services/tutor/profile";

export const TutorContext = createContext();

export const TutorProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [tutor, setTutor] = useState(null);
  const [loading, setLoading] = useState(true);

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const socketRef = useRef(null);

  // Mirrors AuthContext's approvalPending for childcare — here it tracks
  // profile completion (tutors.is_profile_complete) rather than admin
  // approval, but drives the same Dashboard gating pattern. Fetched once
  // per login here in context, not from the Dashboard component itself.
  const [approvalPending, setApprovalPending] = useState(false);
  // True until the first registration-status check resolves (or is skipped
  // because there's no logged-in tutor). Consumers must wait for this to go
  // false before trusting approvalPending — otherwise its default value
  // (false) can let a page fire a real-data fetch before we actually know
  // registration is incomplete, surfacing a raw API error instead of guidance.
  const [approvalPendingLoading, setApprovalPendingLoading] = useState(true);
  const [adminVerified, setAdminVerified] = useState(false);

  const loadUserFromToken = () => {
    const token = localStorage.getItem("tutorToken") || sessionStorage.getItem("tutorToken");

    if (!token) {
      setIsLoggedIn(false);
      setTutor(null);
      setLoading(false);
      return;
    }

    try {
      const decoded = jwtDecode(token);
      setTutor({
        id: decoded.id,
        fname: decoded.fname,
        email: decoded.email,
        role: decoded.role,
      });
      setIsLoggedIn(true);
    } catch (err) {
      console.error("Failed to decode tutor token:", err);
      setIsLoggedIn(false);
      setTutor(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUserFromToken();
  }, []);

  // Fetch profile-completion status once per login — cached in context so
  // the Dashboard never needs to call this itself.
  const refreshApprovalPending = async () => {
    setApprovalPendingLoading(true);
    try {
      const res = await getTutorProfileStatus();
      setApprovalPending(!res.data?.data?.isComplete);
      setAdminVerified(!!res.data?.data?.adminVerified);
    } catch (_) {
      // Fail-open: don't block the dashboard if the status check errors out.
      setApprovalPending(false);
      setAdminVerified(false);
    } finally {
      setApprovalPendingLoading(false);
    }
  };

  useEffect(() => {
    // Wait for token decoding (the `loading` flag above) to settle first —
    // otherwise this can run once with a stale `tutor === null` closure and
    // flip approvalPendingLoading to false a tick before `tutor` is actually
    // set, letting consumers fetch real data too early.
    if (loading) return;
    if (!tutor) {
      setApprovalPending(false);
      setApprovalPendingLoading(false);
      return;
    }
    refreshApprovalPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tutor?.id, loading]);

  // Connect socket and fetch notifications when tutor logs in
  useEffect(() => {
    if (!tutor) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    const token = localStorage.getItem("tutorToken") || sessionStorage.getItem("tutorToken");
    if (!token) return;

    fetch(`${process.env.REACT_APP_BACKEND_URL}/tutors/notifications`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.success) {
          setNotifications(data.notifications);
          setUnreadCount(data.unreadCount);
        }
      })
      .catch(() => {});

    // socket.io-client is loaded on demand — anonymous visitors never pull
    // it into the main bundle.
    let cancelled = false;
    import("socket.io-client").then(({ io }) => {
      if (cancelled) return;

      const socket = io(process.env.REACT_APP_SOCKET_URL, {
        auth: { token },
        transports: ["websocket", "polling"],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 2000,
      });

      socket.on("notification:new", (notif) => {
        setNotifications((prev) => [notif, ...prev]);
        setUnreadCount((prev) => prev + 1);
      });

      socketRef.current = socket;
    });

    return () => {
      cancelled = true;
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [tutor]);

  const markAllRead = async () => {
    const token = localStorage.getItem("tutorToken") || sessionStorage.getItem("tutorToken");
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/tutors/notifications/mark-read`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
      setUnreadCount(0);
    } catch (_) {}
  };

  const markOneRead = async (id) => {
    const token = localStorage.getItem("tutorToken") || sessionStorage.getItem("tutorToken");
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/tutors/notifications/${id}/read`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: 1 } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (_) {}
  };

  const login = (token, rememberMe = true) => {
    localStorage.setItem("role", "tutor");
    if (rememberMe) {
      localStorage.setItem("tutorToken", token);
      sessionStorage.removeItem("tutorToken");
    } else {
      sessionStorage.setItem("tutorToken", token);
      localStorage.removeItem("tutorToken");
    }
    loadUserFromToken();
    window.dispatchEvent(new Event("userChanged"));
  };

  const logout = () => {
    localStorage.removeItem("tutorToken");
    sessionStorage.removeItem("tutorToken");
    localStorage.removeItem("role");
    setIsLoggedIn(false);
    setTutor(null);
    setNotifications([]);
    setUnreadCount(0);
  };

  return (
    <TutorContext.Provider value={{
      isLoggedIn, tutor, login, logout, loading,
      notifications, unreadCount, markAllRead, markOneRead,
      approvalPending, approvalPendingLoading, refreshApprovalPending,
      adminVerified,
    }}>
      {children}
    </TutorContext.Provider>
  );
};

export const useTutor = () => React.useContext(TutorContext);